import { type ReactElement, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import useSWR from 'swr';
import { parseCookies } from 'nookies';
import { captureException, ErrorBoundary, type FallbackRender } from '@sentry/nextjs';
import {
  SnackbarProvider,
  closeSnackbar,
  type SnackbarKey,
} from 'notistack';

import {
  Alert, Box, Button,
  Portal,
  Snackbar,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import ErrorFallback from '../error-fallback';

// const Header = dynamic(() => import('../components/header'), {
//   loading: () => <header />,
// });

const Header = dynamic(() => import('../header/old-header'), {
  ssr: false,
  loading: () => <header style={{ height: 90 }} />,
});

const Footer = dynamic(() => import('../footer'), {
  loading: () => <footer />,
});

function CloseSnackbarButton(snackbarId: SnackbarKey): ReactNode {
  return (
    <Box sx={{ pl: 2, borderLeft: '1px solid #fff' }}>
      <Button
        size="small"
        color="white"
        variant="outlined"
        onClick={() => closeSnackbar(snackbarId)}
      >
        <CloseIcon />
        Dismiss
      </Button>
    </Box>
  );
}

const FallbackComponent: FallbackRender = function fallbackRender({
  error,
  resetError,
}) {
  return (
    <>
      <Portal>
        <Snackbar
          style={{ zIndex: 9999 }}
          autoHideDuration={10000}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={!!error}
        >
          <Alert severity="error" onClose={resetError}>
            <Typography variant="caption">{(error as Error).toString()}</Typography>
          </Alert>
        </Snackbar>
      </Portal>
      <ErrorFallback />
    </>
  );
};

export default function Layout({ children }: { children: ReactNode }): ReactElement {
  const { data: user } = useSWR<Account>(parseCookies().qlp_user_token_set
    ? '/api/v1/auth/me' : null);
  return (
    <>
      <Header user={user} />
      <SnackbarProvider
        autoHideDuration={10000}
        maxSnack={5}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        action={CloseSnackbarButton}
      >
        <ErrorBoundary
          fallback={FallbackComponent}
          onError={(error) => captureException(error)}
        >
          {children}
        </ErrorBoundary>
      </SnackbarProvider>
      <Footer />
    </>
  );
}
